/**
 * Phase 6 — cross-component handoff dispatcher for every v2 form-like submit response.
 *
 * Callers invoke at the top of their submit-response handler; if we return {@code true}
 * the browser is navigating and the caller should short-circuit. Otherwise the response
 * is treated as the caller's normal path (form merge, completion, etc.).
 *
 * The server attaches a {@code handoff} sub-object on:
 *   - {@code DynamicFormController.submit} (standalone dynamic_form v2 submit)
 *   - {@code DynamicReviewTaskController} form-submitted callback
 *   - {@code FormFlowController.submit} (flow-member submit — already handled in useFormFlow)
 *
 * Supported outcomes:
 *   CROSS_FLOW_HANDOFF   — navigate to the explicit {@code handoffUrl}.
 *   CROSS_FLOW_NEXT_STEP — navigate to {@code /form-flow/{formSessionId}}.
 *   TRANSITIONING        — navigate to {@code /form-flow/{transitioningFormSessionId ?? formSessionId}};
 *                          the shell subscribes SSE + polls until step_ready.
 *   Anything else (NEXT_STEP / BACK_STEP / COMPLETION / missing) → no-op, caller continues.
 *
 * @param {object} response — the submit response body from the engine.
 * @returns {boolean} true when this function navigated (caller should return).
 */
export function handleHandoffEnvelope(response) {
  const handoff = response?.handoff;
  if (!handoff || !handoff.outcome) return false;

  switch (handoff.outcome) {
    case 'CROSS_FLOW_HANDOFF': {
      if (!handoff.handoffUrl) {
        console.error(
          'handleHandoffEnvelope: CROSS_FLOW_HANDOFF missing handoffUrl; staying put.',
        );
        return false;
      }
      window.location.assign(handoff.handoffUrl);
      return true;
    }
    case 'CROSS_FLOW_NEXT_STEP': {
      if (!handoff.formSessionId) return false;
      window.location.assign(`/form-flow/${handoff.formSessionId}`);
      return true;
    }
    case 'TRANSITIONING': {
      const target = handoff.transitioningFormSessionId || handoff.formSessionId;
      if (!target) return false;
      window.location.assign(`/form-flow/${target}`);
      return true;
    }
    default:
      return false;
  }
}

export default handleHandoffEnvelope;
